import { useEffect, useState } from "react";
import { create } from "zustand";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { ProductUpsellGroup } from "@/lib/products";
import { getUpsellPriceForOrderType } from "@/lib/products";
import type { OrderTypes } from "@/lib/supabase/enums";
import { cn, formatNumberToCurrency } from "@/lib/utils";

/** One accepted offer, ready to become its own cart line. */
export interface UpsellSelection {
  productId: string;
  name: string;
  price: number;
}

interface UpsellDialogOptions {
  groups: ProductUpsellGroup[];
  orderType: OrderTypes;
}

interface UpsellDialogState {
  isOpen: boolean;
  options: UpsellDialogOptions | null;
  resolve: ((value: UpsellSelection[]) => void) | null;
}

const useUpsellDialogStore = create<UpsellDialogState>(() => ({
  isOpen: false,
  options: null,
  resolve: null,
}));

/**
 * Promise-based upsell step, built like useConfirmDialog so it can be awaited
 * from anywhere that adds to the cart — the quick-add button on the card, the
 * product dialog, and the menu dialog all go through the same question.
 *
 * Resolves with the accepted offers, or an empty array when the customer
 * declines or dismisses. Declining is never an error: the product they already
 * configured has been added either way.
 */
export function useUpsellDialog() {
  const showUpsells = (
    options: UpsellDialogOptions,
  ): Promise<UpsellSelection[]> =>
    new Promise((resolve) => {
      useUpsellDialogStore.setState({ isOpen: true, options, resolve });
    });

  return { showUpsells };
}

export function UpsellDialogProvider() {
  const { t } = useTranslation("translation");
  const { isOpen, options, resolve } = useUpsellDialogStore();

  // itemId -> groupId, so a group's maximum can be enforced without working
  // out which group a tick came from on every change.
  const [selected, setSelected] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) setSelected({});
  }, [isOpen]);

  const handleClose = (selections: UpsellSelection[]) => {
    useUpsellDialogStore.setState({ isOpen: false });
    resolve?.(selections);
    // Cleared late so the closing animation still has its content.
    setTimeout(() => {
      useUpsellDialogStore.setState({ options: null, resolve: null });
    }, 200);
  };

  if (!options) return null;

  const { groups, orderType } = options;

  const toggle = (group: ProductUpsellGroup, itemId: string) => {
    setSelected((current) => {
      if (current[itemId]) {
        const next = { ...current };
        delete next[itemId];
        return next;
      }

      const next = { ...current };
      const max = group.maxSelections > 0 ? group.maxSelections : Infinity;
      const inGroup = Object.entries(current)
        .filter(([, groupId]) => groupId === group.id)
        .map(([id]) => id);

      // A full group drops its oldest pick, so ticking a box always does
      // something visible rather than silently refusing.
      if (inGroup.length >= max) {
        for (const id of inGroup.slice(0, inGroup.length - max + 1)) {
          delete next[id];
        }
      }

      next[itemId] = group.id;
      return next;
    });
  };

  const collectSelections = (): UpsellSelection[] => {
    const selections: UpsellSelection[] = [];
    for (const group of groups) {
      for (const item of group.items) {
        if (selected[item.id] !== group.id) continue;
        const price = getUpsellPriceForOrderType(item, orderType);
        if (price === undefined) continue;
        selections.push({ productId: item.productId, name: item.name, price });
      }
    }
    return selections;
  };

  const selections = collectSelections();
  const selectedTotal = selections.reduce(
    (total, selection) => total + selection.price,
    0,
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose([]);
      }}
    >
      <DialogContent className="p-0 md:max-h-[85dvh] max-h-[100dvh] rounded-none md:rounded-lg flex flex-col overflow-hidden max-w-full md:max-w-lg">
        <DialogHeader className="p-4 shrink-0 text-left">
          <DialogTitle className="text-xl font-bold">
            {t("Upsells.title")}
          </DialogTitle>
          <DialogDescription>{t("Upsells.description")}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 overflow-auto">
          {groups.map((group) => (
            <div key={group.id} className="flex flex-col gap-2">
              <div className="bg-gray-100 px-4 py-3">
                <span className="font-bold text-lg">{group.title}</span>
                {group.description && (
                  <p className="text-sm text-gray-500">{group.description}</p>
                )}
              </div>
              <div className="px-4 flex flex-col">
                {group.items.map((item, index) => {
                  const price = getUpsellPriceForOrderType(item, orderType);
                  if (price === undefined) return null;
                  const isSelected = selected[item.id] === group.id;

                  return (
                    // Label and checkbox as siblings, never nested: a Radix
                    // checkbox renders a button, and a button inside a
                    // htmlFor label fires the toggle twice.
                    <div
                      key={item.id}
                      className={cn(
                        "flex items-center gap-3 py-3 px-2",
                        index < group.items.length - 1 && "border-b",
                      )}
                    >
                      {item.featuredImage && (
                        <img
                          src={item.featuredImage}
                          alt=""
                          className="size-14 shrink-0 rounded-lg border object-cover"
                          loading="lazy"
                        />
                      )}
                      <Label
                        htmlFor={item.id}
                        className="flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-0.5 font-normal"
                      >
                        <span className="text-lg">{item.name}</span>
                        {item.description && (
                          <span className="truncate text-sm text-gray-500">
                            {item.description}
                          </span>
                        )}
                      </Label>
                      <span className="shrink-0 text-gray-500 text-sm font-semibold">
                        {`+ ${formatNumberToCurrency({ number: price })}`}
                      </span>
                      <Checkbox
                        id={item.id}
                        checked={isSelected}
                        onCheckedChange={() => toggle(group, item.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t shadow p-4 flex flex-row gap-3 justify-between shrink-0">
          <button
            type="button"
            onClick={() => handleClose([])}
            className="rounded-full border px-4 py-2 font-bold text-gray-600 cursor-pointer hover:bg-gray-50"
          >
            {t("Upsells.no_thanks")}
          </button>
          <button
            type="button"
            disabled={selections.length === 0}
            onClick={() => handleClose(selections)}
            className="bg-black/90 cursor-pointer hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed flex-1 rounded-full py-2 px-4 text-white font-bold text-lg"
          >
            {t("Upsells.add")}
            {selections.length > 0
              ? ` - ${formatNumberToCurrency({ number: selectedTotal })}`
              : ""}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
